import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Coins, Zap, Flame, Users, Wallet, Gamepad2, Copy, ArrowRight, Sparkles } from "lucide-react";
import { api, fmtCoins, fmtUsd, coinsToUsd, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";

export default function Dashboard() {
  const { user, setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [txns, setTxns] = useState([]);
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    api.get("/transactions").then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  const usd = coinsToUsd(user?.coins);
  const payoutProgress = Math.min((usd / 5) * 100, 100);
  const today = new Date().toISOString().slice(0, 10);
  const claimedToday = user?.last_claim && String(user.last_claim).slice(0, 10) === today;

  const claim = async () => {
    setClaiming(true);
    try {
      const { data } = await api.post("/user/claim-daily");
      setUser(data.user);
      toast.success(`+${data.reward} coins · Day ${data.streak} streak 🔥`);
      const r = await api.get("/transactions");
      setTxns(r.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };

  const copyRef = () => {
    navigator.clipboard.writeText(`${window.location.origin}/login?ref=${user?.referral_code}`);
    toast.success("Referral link copied!");
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <p className="eyebrow mb-1">/ COMMAND CENTER</p>
        <h1 className="font-display font-black text-2xl sm:text-3xl">
          Welcome back, <span className="text-cyan-neon">{user?.name?.split(" ")[0] || "Earner"}</span>
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="neon-card p-5">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Balance</span>
            <Coins className="w-5 h-5 text-amber-neon" />
          </div>
          <p className="font-mono-num font-bold text-3xl mt-3" data-testid="dash-coin-balance">{fmtCoins(user?.coins)}</p>
          <p className="text-lime-neon font-mono-num text-sm mt-1">{fmtUsd(usd)} USD</p>
          <p className="text-xs text-slate-500 mt-3">1,000 coins = $1.00</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="neon-card p-5">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Daily Streak</span>
            <Flame className="w-5 h-5 text-orange-400" />
          </div>
          <p className="font-mono-num font-bold text-3xl mt-3">Day {user?.streak || 0}</p>
          <Button
            onClick={claim}
            disabled={claiming || claimedToday}
            data-testid="daily-streak-claim-button"
            className="mt-3 w-full btn-neon h-9"
          >
            {claimedToday ? "Claimed today ✓" : claiming ? "Claiming…" : "Claim daily bonus"}
          </Button>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="neon-card p-5">
          <div className="flex items-center justify-between">
            <span className="eyebrow">Referrals</span>
            <Users className="w-5 h-5 text-[#FF007A]" />
          </div>
          <p className="text-sm text-slate-400 mt-3">Earn 15% of what friends make.</p>
          <div className="mt-3 flex items-center gap-2 bg-[#07090E] border border-slate-700 rounded-lg px-3 py-2">
            <span className="font-mono-num text-xs text-cyan-neon truncate flex-1">{user?.referral_code}</span>
            <button onClick={copyRef} data-testid="copy-referral-button" className="text-slate-400 hover:text-white">
              <Copy className="w-4 h-4" />
            </button>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="neon-card profit-card p-5">
          <div className="flex items-center justify-between">
            <span className="eyebrow">PayPal Cashout</span>
            <Wallet className="w-5 h-5 text-lime-neon" />
          </div>
          <p className="font-mono-num font-bold text-2xl mt-3 text-lime-neon">{fmtUsd(usd)}</p>
          <Progress value={payoutProgress} className="mt-3 h-2 bg-slate-800" />
          <p className="text-xs text-slate-500 mt-2">{usd >= 5 ? "Ready to cash out!" : `${fmtUsd(5 - usd)} to min $5`}</p>
          <Button onClick={() => navigate("/cashout")} data-testid="dash-cashout-button" className="mt-3 w-full btn-neon h-9">
            Cash out <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </motion.div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          onClick={() => navigate("/play-earn")}
          data-testid="quick-play-earn"
          className="neon-card p-6 text-left group"
        >
          <Gamepad2 className="w-8 h-8 text-cyan-neon" />
          <h3 className="font-display font-bold text-lg mt-3 group-hover:text-cyan-neon transition">Play & Earn</h3>
          <p className="text-slate-400 text-sm mt-1">Complete verified Spawntap offers to earn coins.</p>
        </motion.button>
        <motion.button
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.05 }}
          onClick={() => navigate("/cashout")}
          data-testid="quick-cashout-tile"
          className="neon-card profit-card p-6 text-left group"
        >
          <Wallet className="w-8 h-8 text-lime-neon" />
          <h3 className="font-display font-bold text-lg mt-3 group-hover:text-lime-neon transition">Cash Out</h3>
          <p className="text-slate-400 text-sm mt-1">Withdraw your balance to PayPal from $5.</p>
        </motion.button>
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="neon-card p-6"
        >
          <Sparkles className="w-8 h-8 text-amber-neon" />
          <h3 className="font-display font-bold text-lg mt-3">Need help?</h3>
          <p className="text-slate-400 text-sm mt-1">Tap the chat bubble for AI support anytime.</p>
        </motion.div>
      </div>

      <div className="neon-card p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-bold text-lg">Recent Activity</h3>
          <span className="eyebrow">{txns.length} entries</span>
        </div>
        {txns.length === 0 ? (
          <p className="text-slate-500 text-sm py-6 text-center">No activity yet — complete a Spawntap offer to start earning!</p>
        ) : (
          <div className="divide-y divide-slate-800/70">
            {txns.slice(0, 10).map((t) => (
              <div key={t.txn_id} className="flex items-center justify-between py-3" data-testid="txn-row">
                <div>
                  <p className="text-sm text-slate-200">{t.description}</p>
                  <p className="text-xs text-slate-500 mt-0.5 capitalize">{t.kind} · {new Date(t.created_at).toLocaleDateString()}</p>
                </div>
                <span className={`font-mono-num font-semibold text-sm ${t.coins >= 0 ? "text-lime-neon" : "text-[#FF007A]"}`}>
                  {t.coins >= 0 ? "+" : ""}{fmtCoins(t.coins)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
